
import React, { useState,  useRef, useEffect } from 'react';
import { Popover } from '@headlessui/react'
import { usePopper } from 'react-popper'
import { cls as clsn } from 'views/NUI/utils'
import { Link } from "react-router-dom";
import menu_model from 'models/menu_model'
import SIcon from '../../views/NUI/StreamlineIcons/SIcon';

// Side Section
const intro = {
    about: {
                title: 'About Us',
                desc: `Founded in Melbourne, WeWordle has helped hundreds of thousands of players enjoy word puzzles and
                multiplayer word games from anywhere in the world.`
            },
    certs: {
                title: 'Achievements',
                desc: `Track your achievements and unlock badges as you play. Our achievement system
				rewards consistent play and skill improvement. Choose from multiple achievement categories.`
            },
    telehealth:   {
                title: 'Multiplayer Battles',
                desc: `Challenge friends and players worldwide in real-time word battles. Our
				multiplayer mode is available 24/7 with instant matchmaking.`
            },
    scripts:    {
                title: 'Game Modes',
                desc: `Browse our game modes to find your challenge. Choose from daily puzzles, multiplayer battles,
				and timed challenges. Track your progress on leaderboards and compete with friends worldwide.`
            },
    do:     {
                title: 'What we do',
                desc: `WeWordle is the leading multiplayer word puzzle game. We offer a range of game modes
                including daily challenges and multiplayer battles, available 24/7.`
            },
    treat: {
                title: 'Game Features',
                desc: `WeWordle provides exciting word puzzle experiences for players worldwide.
				Players can access multiple game modes, leaderboards, and daily challenges.`
            }
}

const intro_links = {
    treat: [{ title: 'Ready to play?', link_text: 'Start Playing', link: '/cat' }, ],
    do: [{ title: 'Not sure where to start?', link_text: 'Start Playing', link: '/cat' }],
    scripts: [{ title: 'Ready for a challenge?', link_text: 'Play Now', link: '/cat' }],
    certs: [{ title: 'Want to track your progress?', link_text: 'View Achievements', link: '/c/pharm/certs/' }],
	telehealth: [{ title: 'Want to challenge a friend?', link_text: 'Start Multiplayer', link: '/cat/DocCons' }],
}

// Main Section
const tiles = {
	about: [
		{ title: 'Our story', desc: 'Find out more about the leading multiplayer word game.', link: '/about-us' },
		{ title: 'FAQ', desc: 'Get answers to our players\' most frequently asked questions.', link: '/faq' },
		{ title: 'Help centre', desc: 'Access help and support on our game from our team.', link: 'https://help.wewordle.com/en/' },
		{ title: 'Our team', desc: 'Read more about the team behind WeWordle.', link: '/our-team' },
		{ title: 'Careers', desc: 'Would you like to work with us? Find out more.', link: '/careers', },
		{ title: 'News', desc: 'Get the latest game updates and news.', link: 'https://www.wewordle.com/blog' },
		{ title: 'Privacy', desc: 'Read our privacy policy and understand our security protocol.', link: '/privacy' },
		{ title: 'Contact us', desc: 'Contact our friendly team who are always more than happy to help.', link: '/contact-us' },
		{ title: 'For Partners', desc: 'See how you can partner with WeWordle.', link: '/for-pharmacies' },
		{ title: 'How to videos', desc: 'Not sure how to get started? Watch our quick, easy how-to videos.', link: '/how-to-videos/' },
	],
	do: [
		{ title: 'Daily Challenge', desc: 'Play the daily word puzzle challenge.', link: '/cat' },
		{ title: 'Multiplayer Battles', desc: 'Challenge players in real-time word battles.', link: '/cat/DocCons' },
		{ title: 'Achievements', desc: 'Track your progress and unlock achievements.', link: '/c/pharm/certs/' },
		{ title: 'Tournament Mode', desc: 'Compete in weekly tournaments for prizes.', link: 'https://www.wewordle.com/treatment-plans/', new: true },
		{ title: 'Practice Mode', desc: 'Practice and improve your word skills.', link: '/c/pharm/patho' },
		{ title: 'Leaderboards', desc: 'See how you rank against other players.', link: '/c/pharm/express-specialist-referrals' },
		{ title: 'Friends & Teams', desc: 'Play with friends and join teams.', link: '/childrens-doctor-online/' },
		{ title: 'Game Stats', desc: 'View detailed statistics about your gameplay.', link: '/mental-health-services/' },
	]
}

const image_links = {
	scripts:  [
		{
			img:'https://storage.googleapis.com/instant-med-public/megamenu/is-online-scripts.jpg',
			alt: 'person holding phone',
			title: 'Game Modes',
			desc: 'Find out how our different game modes work.',
			link: '/online-scripts',
			link_text: 'Find out more',
		},
		{
			img: 'https://storage.googleapis.com/instant-med-public/megamenu/girl-on-phone-with-medicine.jpg',
			alt: 'player on phone',
			title: 'Find your challenge',
			desc: 'Browse our catalogue of word puzzle challenges.',
			link: '/cat',
			link_text: 'Browse games',
		},
	],
	telehealth: [
		{
			img: 'https://storage.googleapis.com/instant-med-public/megamenu/doctor-andy.jpg',
			alt: 'WeWordle team',
			title: 'Multiplayer Battles',
			desc: 'Learn more about our multiplayer game modes.',
			link: '/telehealth-consultations/',
			link_text: 'Find out more',
		},
		{
			img: 'https://storage.googleapis.com/instant-med-public/megamenu/young-guy-talking-on-phone.jpg',
			alt: 'player on phone',
			title: 'Game modes we offer',
			desc: 'View our range of multiplayer battle modes.',
			link: '/cat/DocCons',
			link_text: 'View multiplayer modes',
		},
		{
			img: 'https://storage.googleapis.com/instant-med-public/megamenu/iStock-1186116325.jpg',
			alt: 'player enjoying game',
			title: 'Friends and teams',
			desc: 'Play with friends and join competitive teams.',
			link: '/childrens-doctor-online/',
			link_text: 'Find out more',
		},
	],
	certs: [
		{
			img: 'https://storage.googleapis.com/instant-med-public/megamenu/guy-looking-at-phone-smiling.jpg',
			alt: 'player on phone',
			title: 'About achievements',
			desc: 'Find out how you can unlock achievements.',
			link_text: 'Find out more',
			link: '/online-medical-certificates/',
		},
		{
			img: 'https://storage.googleapis.com/instant-med-public/megamenu/med-cert-icon-blue-bg.jpg',
			alt: 'achievement icon',
			title: 'Achievements we offer',
			desc: 'View our range of achievements and badges.',
			link_text: 'View achievements',
			link: '/c/pharm/certs/',
		},
		{
			img:'https://storage.googleapis.com/instant-med-public/megamenu/is-medical-certificate.jpg',
			alt: 'leaderboard',
			title: 'Leaderboard rankings',
			desc: 'Find out about our competitive ranking system.',
			link_text: 'Find out more',
			link: '/online-medical-certificates-employers/',
		},
	],
}

const list = {
	treat: [{
		title: 'Game Categories',
		list: [
			{name: 'Daily Challenge', link:'-LMrOQSVQMBVgUqe3y2A'},
			{name: 'Quick Match', link:'-M34DQ_Bi2Qs7693U5-H'},
			{name: 'Multiplayer Battle', link:'-LJVyR5Pdls_ydzW8sRk'},
			{name: 'Tournament', link:'-LJlvQo2RunU9oi9abk5'},
			{name: 'Practice Mode', link:'-LJcGNXMyADt_gxi2m-f'},
			{name: 'Timed Challenge', link:'womens/contraception'},
			{name: 'Word Sprint', link:'-N9YuK8ZZsDznRtdISNF'},
			{name: 'Puzzle Rush', link:'-LTmNvHxxWf4NuDd8rMn'},
			{name: 'Word Chain', link:'-LMrOQSVQMBVgUqe3y2A'},
			{name: 'Anagram Mode', link:'mens/erectile-dysfunction'},
			{name: 'Classic Mode', link:'-LTLTLfVHJG0fyp2GO44'},
			{name: 'Hard Mode', link:'mens/hair'},
			{name: 'Expert Level', link:'-LOJ8nO6CQ9dJLu7lTIo'},
			{name: 'Team Battle', link:'-LJcNCNA4ONtItNNzXB0'},
			{name: 'Weekly Challenge', link:'-LMfGlpF3oP3xgL52d7w'},
			{name: 'Leaderboard', link:'womens/nausea'},
			{name: 'Achievements', link:'womens/hrt'},
			{name: 'Friends Match', link:'-N5XUiCWSnqlAA_5snc0'},
			{name: 'Random Opponent', link:'-LJn8ZK_ufxt8fWpDyl8'},
			{name: 'Ranked Play', link:'mens/premature-ejaculation'},
			{name: 'Casual Play', link:'-LJcNCNA4ONtItNNzXB0'},
			{name: 'Streaks', link:'-LQ-1WaFZZIdgfFLBsZC'},
			{name: 'Stats', link:'-LwGrL-z9Tx_cVxQH088'},
			{name: 'Settings', link:'-LJVyeBvRW_BnUCsx0kw'},
		],
		link_text: 'View all game modes',
		link: '/cat'}
	]
}

// 		--------------------------------		--------------------------------		---------

const MegaMenu = ({inSideMenu, cls='main', setMainSideMenu }) => {
	const [current, setCurrent] = useState();
	const [referenceElement, setReferenceElement] = useState()
	const [sideMegaMenu, setSideMegaMenu] = useState(true)
	const [popperElement, setPopperElement] = useState()
	const {styles, attributes} = usePopper(referenceElement, popperElement)
	const [mainMenu, setMenu] = useState([])
	const [panel, setPanel] = useState()

	useEffect(() => {
		const menu = menu_model.get_megamenu(inSideMenu, '/');
		setMenu(menu)
	}, [inSideMenu])
	
	const handleShowMenu = (val) => {
		setSideMegaMenu(val)
		setMainSideMenu?.(val)
	}

	const handleOpen = (open) => {
		useEffect(() => {
			!open && handleShowMenu(true)
		}, [open])
	}

	// 		--------------------------------		--------------------------------		---------

	useEffect(() => {
		setPanel(
		    <Popover.Panel 
				ref={popperElement}
				className={clsn(inSideMenu ? "side-menu h-full overflow-y-auto pb-24 mt-[72px]" 
				: "lg:px-9 mt-6 bg-is-gray-20", "panel bg-white w-full shadow-lg",)}
				style={styles.popper}
				{...attributes.popper}
				>
				<div className={clsn(!inSideMenu && "md:grid-cols-3 p-4 sm:px-8 md:py-10 lg:p-10 gap-4", "relative grid w-full grid-cols-1 mx-auto max-w-screen-menu-xl")}>
					{/* side section */}
					<div className={clsn(inSideMenu && "px-4 py-6", "section-side col-span-1 h-full")}>
						{intro[current] && 
							<>
								<h1 className="text-is-blue">{intro[current].title}</h1>
								<p className={clsn(!inSideMenu && "xl:w-[90%]")}>{intro[current].desc}</p>
							</>
						}

						{intro_links[current] && 
							<div className="flex flex-col lg:w-3/4">
								{intro_links[current].map((el, i) => {
									return  <div key={i}>
												<h5 className="text-black">{el.title}</h5>
												<Popover.Button 
													as={Link} to={el.link}
													className="primary ui button p-4 w-auto">
													{el.link_text}
												</Popover.Button>
											</div>
									})}
							</div>
						}
					</div>

					{/* main section */}
					<div className="section-main col-span-2 h-full">
						{tiles[current] &&
							<div className={clsn(!inSideMenu ? "sm:grid-cols-2 lg:grid-cols-3 pb-[80px] md:pb-0 gap-1" : "px-2", "tiles grid grid-cols-1")}>
							{tiles[current].map((el, i) => {
								const content = <><span className="flex justify-between items-stretch mb-2">
													<span>
													<p className="font-bold mt-0">{el.title}</p></span>
													<span className="flex items-start">
													{el.new && <span className="bg-is-blue text-white text-xs p-2 font-bold rounded-[1.25rem] max-h-5 mx-1 mt-1 flex items-center">New</span>}
													{el.ext ? <SIcon size="xs" name="external" cls="text-is-black hover:text-is-blue" bold/> :
													<SIcon size="xs" name="chevron" svg_cls="-rotate-90" cls="text-is-black hover:text-is-blue" bold/>}</span>
												</span>
												<p className="w-[90%] desc">{el.desc}</p>
												</>
								
								return  <Popover.Button 
											as={el.link?.startsWith('https://') ? 'a' : Link} href={el.link}
											key={i} to={el.link}
											className={clsn(inSideMenu && "border-t border-is-50 rounded-none", "hover:border-transparent tile col-span-1 text-is-black h-full p-4 cursor-pointer rounded-lg hover:bg-is-blue-20 min-h-[104px]")} 
											onClick={() => {inSideMenu && handleShowMenu(true)}}
											>
										{content}
										</Popover.Button>
								})}
							</div>
						}

						{image_links[current] && 
							<div className={clsn(inSideMenu ? "grid grid-cols-1 border-t border-is-50 pb-[80px]" : "grid grid-cols-1  md:grid-cols-2 lg:grid-cols-3 gap-6")}>
								{image_links[current].map((el, i) => {
									return  <div key={i} className={clsn(inSideMenu && "border-b border-is-50", "image-link col-span-1 text-is-black hover:text-is-blue h-full w-full")}>
												<Popover.Button
													as={el.link?.startsWith('https://') ? 'a' : Link} to={el.link} href={el.link}
													onClick={() => {inSideMenu && handleShowMenu(true)}}
													className="block text-is-black">
													<div style={{backgroundImage: `url(${el.img})`}} className={`${inSideMenu ? "hidden" : "pt-[66%]"} bg-cover rounded-md mb-2`}></div>
													<h4 className="font-700 mt-0">{el.title}</h4>
													<p className="md:min-h-[52px]">{el.desc}</p>
													<span className="info-link text-is-blue mr-2 hover:text-is-black" href={el.link}>{el.link_text}</span>
												</Popover.Button>
											</div>
								})}
							</div>
						}

						{list[current] && list[current].map((el, i) => {
							return  <div key={i} className={clsn(inSideMenu && "pb-[80px]", "list text-is-black hover:text-is-black rounded-md w-full")}>
									<h4 className={clsn('px-4', !inSideMenu && 'sm:px-0')}>{el.title}</h4>
									<div className={clsn(!inSideMenu ? "md:h-[650px] lg:h-[410px]": "h-full", "list flex flex-wrap flex-col")}>
									{el.list.map((el, i) => 
											<Popover.Button 
												key={i} as={Link} to={`/c/pharm/${el.link}`}
												onClick={() => {inSideMenu && handleShowMenu(true)}} 
												className="mr-4">
												{el.name}
											</Popover.Button>)
										}
										<span className='flex'>
											<Popover.Button className={clsn("info-link text-is-blue hover:text-is-black", !inSideMenu && "mr-2")} as={Link} to={el.link}><span>{el.link_text}</span></Popover.Button>
										</span>
									</div>
								</div>
							})
						}
					</div>
				</div>
			</Popover.Panel>)
	}, [current])

   // 		--------------------------------		--------------------------------		---------

   return (
	<Popover.Group className={`flex justify-end w-full mx-auto mega-menu ${inSideMenu ? "flex-col" : "flex-row items-center mr-2"}`}>

	{mainMenu.length > 0  && mainMenu?.filter(Boolean).map((el, i) => {
	  return <Popover key={i}>
		{({ open, close }) => (

		  <>
		    {/* Main Menu */}
			{inSideMenu && handleOpen(open)}

			{inSideMenu && !sideMegaMenu && 
				<span className="text-is-black hover:text-is-blue"><button onClick={() => {
					handleShowMenu(true)
					close(referenceElement)}} 
					className="btn back flex items-center bg-none w-1/2 absolute top-[8px] left-[8px] py-4 cursor-pointer"
					>
					<SIcon name="chevron" size="xxs" svg_cls='rotate-90 mr-1' bold/>
					Back
				</button></span>
			}

		    <div className="relative z-10">
				<div className={clsn(inSideMenu ? "px-0": "xl:px-2")}>
					<Popover.Button
						ref={setReferenceElement}
						key={i}
						onClick={() => { 
							setCurrent(el.key)
							inSideMenu && handleShowMenu(false)
						}}
						className={clsn(cls, el.cls,
						inSideMenu && !sideMegaMenu && "hidden",
						!inSideMenu && "items-center",
						inSideMenu ? "side-btn w-full p-4 hover:bg-is-blue-20" : "hover:text-is-blue ml-4 rounded-md",
						open ? "open text-is-blue" : "text-is-black", 
						"popover-btn font-semibold button group flex inline-flex items-end")}>
						{el.name}
						{inSideMenu && <SIcon name="chevron" size="xs" svg_cls='-rotate-90 mr-1' cls="absolute right-4 text-is-black group-hover:text-is-blue" bold/>}
					</Popover.Button>
				</div>
		    </div>

		    {/* Dropdown Menu */}
			{ panel }

		  </>
	 	)}
	  </Popover>     
	})}
	</Popover.Group>

  )
}

export default MegaMenu

